import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/api';
import { printStatement } from '../utils/treasuryExports';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Button, Badge, Card, Input, Select, Textarea, Icon } from '../components/ui';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';
import ExportModal from '../components/ExportModal';
import {
  IconBuildingBank, IconPlus, IconTrash, IconFilter, IconPrinter,
  IconFileExport, IconCalendar, IconUser,
  IconArrowDown, IconArrowUp, IconX,
} from '@tabler/icons-react';

export default function BankPage() {
  const { activeWorkspace } = useWorkspace();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [stats, setStats] = useState(null);

  // ─── Filtres ─────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    client: '',
    amountMin: '',
    amountMax: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const getCategoryLabel = (category) => {
    const labels = { sale: 'Vente', purchase: 'Achat', salary: 'Salaire', rent: 'Loyer', utilities: 'Charges', tax: 'Impôts', loan: 'Prêt', investment: 'Investissement', withdrawal: 'Retrait', deposit: 'Dépôt', transfer: 'Virement', other: 'Autre' };
    return labels[category] || category;
  };

  const bankExportColumns = [
    { key: 'date', label: 'Date', accessor: (t) => new Date(t.date).toLocaleDateString('fr-FR') },
    { key: 'description', label: 'Description', accessor: (t) => t.description },
    { key: 'reference', label: 'Référence', accessor: (t) => t.reference || '' },
    { key: 'category', label: 'Catégorie', accessor: (t) => getCategoryLabel(t.category) },
    { key: 'amount', label: 'Montant', accessor: (t) => `${t.transactionType === 'income' ? '+' : '-'}${t.amount.toFixed(3)}` },
  ];

  // Formulaire nouveau compte
  const [accountForm, setAccountForm] = useState({
    name: '',
    bankName: '',
    accountNumber: '',
    rib: '',
    iban: '',
    currency: 'TND',
    initialBalance: 0,
    notes: '',
  });

  // Formulaire transaction
  const [transactionForm, setTransactionForm] = useState({
    transactionType: 'income',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    category: 'other',
    description: '',
    reference: '',
    paymentMethod: 'transfer',
    notes: '',
  });

  useEffect(() => {
    if (activeWorkspace) {
      setSelectedAccount(null);
      loadAccounts();
      loadStats();
    }
  }, [activeWorkspace]);

  useEffect(() => {
    if (selectedAccount) {
      loadTransactions(selectedAccount._id);
      resetFilters();
    }
  }, [selectedAccount]);

  const loadAccounts = async () => {
    if (!activeWorkspace) return;
    try {
      setLoading(true);
      const res = await api.get('/treasury/bank');
      setAccounts(res.data.accounts || []);
      if (res.data.accounts?.length > 0 && !selectedAccount) {
        setSelectedAccount(res.data.accounts[0]);
      }
    } catch (error) {
      console.error('Erreur chargement comptes:', error);
      alert('Erreur lors du chargement des comptes');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (accountId) => {
    try {
      const res = await api.get(`/treasury/bank/${accountId}/transactions`);
      setTransactions(res.data.transactions || []);
    } catch (error) {
      console.error('Erreur chargement transactions:', error);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/treasury/stats');
      setStats(res.data.stats);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  };

  // ─── Filtrage côté client ─────────────────────────────────────
  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      // Filtre date début
      if (filters.dateFrom) {
        const txDate = new Date(tx.date);
        const from   = new Date(filters.dateFrom);
        from.setHours(0, 0, 0, 0);
        if (txDate < from) return false;
      }
      // Filtre date fin
      if (filters.dateTo) {
        const txDate = new Date(tx.date);
        const to     = new Date(filters.dateTo);
        to.setHours(23, 59, 59, 999);
        if (txDate > to) return false;
      }
      // Filtre client (recherche dans description)
      if (filters.client.trim()) {
        const search = filters.client.trim().toLowerCase();
        if (!(tx.description || '').toLowerCase().includes(search)) return false;
      }
      // Filtre montant min
      if (filters.amountMin !== '') {
        if (tx.amount < parseFloat(filters.amountMin)) return false;
      }
      // Filtre montant max
      if (filters.amountMax !== '') {
        if (tx.amount > parseFloat(filters.amountMax)) return false;
      }
      return true;
    });
  }, [transactions, filters]);

  const activeFiltersCount = [
    filters.dateFrom,
    filters.dateTo,
    filters.client.trim(),
    filters.amountMin,
    filters.amountMax,
  ].filter(Boolean).length;

  const resetFilters = () => {
    setFilters({ dateFrom: '', dateTo: '', client: '', amountMin: '', amountMax: '' });
  };

  // ─── Totaux des transactions filtrées ────────────────────────
  const totals = useMemo(() => {
    const income  = filteredTransactions.filter(t => t.transactionType === 'income').reduce((s, t) => s + t.amount, 0);
    const expense = filteredTransactions.filter(t => t.transactionType === 'expense').reduce((s, t) => s + t.amount, 0);
    return { income, expense, net: income - expense };
  }, [filteredTransactions]);

  const handleAddAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post('/treasury/bank', accountForm);
      setShowAddModal(false);
      setAccountForm({ name: '', bankName: '', accountNumber: '', rib: '', iban: '', currency: 'TND', initialBalance: 0, notes: '' });
      loadAccounts();
      loadStats();
      alert('Compte créé avec succès');
    } catch (error) {
      alert('Erreur lors de la création du compte');
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await api.post('/treasury/transactions', {
        ...transactionForm,
        type: 'bank',
        accountId: selectedAccount._id,
        amount: parseFloat(transactionForm.amount),
      });
      setShowTransactionModal(false);
      setTransactionForm({ transactionType: 'income', amount: '', date: new Date().toISOString().split('T')[0], category: 'other', description: '', reference: '', paymentMethod: 'transfer', notes: '' });
      loadTransactions(selectedAccount._id);
      loadAccounts();
      loadStats();
      alert('Transaction ajoutée avec succès');
    } catch (error) {
      alert('Erreur lors de l\'ajout de la transaction');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!await showConfirm('Êtes-vous sûr de vouloir supprimer ce compte ?')) return;
    try {
      await api.delete(`/treasury/bank/${accountId}`);
      loadAccounts();
      loadStats();
      setSelectedAccount(null);
      alert('Compte supprimé avec succès');
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la suppression du compte');
    }
  };

  const handlePrint    = () => { if (!selectedAccount) return; printStatement(selectedAccount, filteredTransactions, 'bank'); };
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(amount);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Icon name="IconBuildingBank" className="text-blue-600" size={28} />
          Comptes Bancaires
        </h1>
        <Button variant="primary" size="md" onClick={() => setShowAddModal(true)}>
          <Icon name="IconPlus" size={18} className="mr-1" />
          Nouveau compte
        </Button>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="border-l-4 border-blue-500 p-6">
            <div className="text-sm text-gray-600">Solde total banque</div>
            <div className="text-2xl font-bold text-gray-800 mt-2">{formatCurrency(stats.totalBank)}</div>
          </Card>
          <Card className="border-l-4 border-green-500 p-6">
            <div className="text-sm text-gray-600">Nombre de comptes</div>
            <div className="text-2xl font-bold text-gray-800 mt-2">{stats.bankAccountsCount}</div>
          </Card>
          <Card className="border-l-4 border-purple-500 p-6">
            <div className="text-sm text-gray-600">Trésorerie totale</div>
            <div className="text-2xl font-bold text-gray-800 mt-2">{formatCurrency(stats.totalTreasury)}</div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des comptes */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Mes comptes</h2>
          <div className="space-y-2">
            {accounts.length === 0 ? (
              <div className="text-center text-gray-500 py-8">Aucun compte bancaire</div>
            ) : (
              accounts.map((account) => (
                <div
                  key={account._id}
                  onClick={() => setSelectedAccount(account)}
                  className={`p-4 rounded-lg cursor-pointer transition ${
                    selectedAccount?._id === account._id
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">{account.name}</div>
                      <div className="text-sm text-gray-600">{account.bankName}</div>
                      <div className="text-xs text-gray-500 mt-1">{account.accountNumber}</div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteAccount(account._id); }}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <Icon name="IconTrash" size={16} />
                    </button>
                  </div>
                  <div className="mt-2 text-right">
                    <div className="text-lg font-bold text-blue-600">{formatCurrency(account.currentBalance)}</div>
                    <Badge variant={account.status === 'active' ? 'success' : 'gray'}>
                      {account.status === 'active' ? 'Actif' : 'Fermé'}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Transactions */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
          {selectedAccount ? (
            <>
              {/* En-tête + boutons */}
              <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                <h2 className="text-lg font-bold text-gray-800">
                  Transactions - {selectedAccount.name}
                </h2>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={showFilters || activeFiltersCount > 0 ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Icon name="IconFilter" size={16} className="mr-1" />
                    Filtres
                    {activeFiltersCount > 0 && <Badge variant="info">{activeFiltersCount}</Badge>}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handlePrint}>
                    <Icon name="IconPrinter" size={16} className="mr-1" />Imprimer
                  </Button>
                  <Button variant="info" size="sm" onClick={() => setShowExportModal(true)}>
                    <IconFileExport size={16} className="mr-1" />Exporter
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setShowTransactionModal(true)}>
                    <Icon name="IconPlus" size={16} className="mr-1" />Ajouter
                  </Button>
                </div>
              </div>

              {/* ─── Panneau Filtres ─── */}
              {showFilters && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <Icon name="IconCalendar" size={14} className="mr-1 text-blue-500" />Date début
                      </label>
                      <Input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <Icon name="IconCalendar" size={14} className="mr-1 text-blue-500" />Date fin
                      </label>
                      <Input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <Icon name="IconUser" size={14} className="mr-1 text-blue-500" />Client / Description
                      </label>
                      <Input type="text" placeholder="Rechercher un client..." value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <Icon name="IconArrowDown" size={14} className="mr-1 text-green-500" />Montant min
                      </label>
                      <Input type="number" step="0.001" placeholder="0" value={filters.amountMin} onChange={(e) => setFilters({ ...filters, amountMin: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <Icon name="IconArrowUp" size={14} className="mr-1 text-red-500" />Montant max
                      </label>
                      <Input type="number" step="0.001" placeholder="∞" value={filters.amountMax} onChange={(e) => setFilters({ ...filters, amountMax: e.target.value })} />
                    </div>
                    <div className="flex items-end">
                      <Button variant="ghost" size="sm" onClick={resetFilters}>
                        <Icon name="IconX" size={16} className="mr-1" />Réinitialiser
                      </Button>
                    </div>
                  </div>

                  {/* Résumé filtres actifs */}
                  {activeFiltersCount > 0 && (
                    <div className="mt-3 pt-3 border-t border-blue-200 flex flex-wrap gap-3 text-xs text-gray-600">
                      <span className="font-medium text-blue-700">
                        {filteredTransactions.length} résultat{filteredTransactions.length > 1 ? 's' : ''} sur {transactions.length}
                      </span>
                      <span className="text-green-600 font-medium">
                        Entrées : {formatCurrency(totals.income)}
                      </span>
                      <span className="text-red-600 font-medium">
                        Sorties : {formatCurrency(totals.expense)}
                      </span>
                      <span className={`font-medium ${totals.net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        Net : {formatCurrency(totals.net)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Tableau */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                          {activeFiltersCount > 0
                            ? 'Aucune transaction ne correspond aux filtres'
                            : 'Aucune transaction'}
                        </td>
                      </tr>
                    ) : (
                      filteredTransactions.map((transaction) => (
                        <tr key={transaction._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(transaction.date).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {transaction.description}
                            {transaction.reference && (
                              <div className="text-xs text-gray-500">Réf: {transaction.reference}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Badge variant="gray">{getCategoryLabel(transaction.category)}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-semibold">
                            <span className={transaction.transactionType === 'income' ? 'text-green-600' : 'text-red-600'}>
                              {transaction.transactionType === 'income' ? '+' : '-'}
                              {formatCurrency(transaction.amount)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {/* Pied de tableau avec totaux */}
                  {filteredTransactions.length > 0 && (
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                      <tr>
                        <td colSpan="3" className="px-4 py-3 text-sm font-semibold text-gray-700">
                          Total ({filteredTransactions.length} transaction{filteredTransactions.length > 1 ? 's' : ''})
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="text-xs text-green-600 font-medium">+{formatCurrency(totals.income)}</div>
                          <div className="text-xs text-red-600 font-medium">-{formatCurrency(totals.expense)}</div>
                          <div className={`text-sm font-bold mt-1 ${totals.net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            {totals.net >= 0 ? '+' : ''}{formatCurrency(totals.net)}
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 py-16">
              Sélectionnez un compte pour voir les transactions
            </div>
          )}
        </div>
      </div>

      {/* Modal Nouveau Compte */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Nouveau Compte Bancaire</h2>
              <form onSubmit={handleAddAccount}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom du compte *</label>
                    <Input type="text" required value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} placeholder="Compte courant BH" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Banque *</label>
                    <Input type="text" required value={accountForm.bankName} onChange={(e) => setAccountForm({ ...accountForm, bankName: e.target.value })} placeholder="BH, BIAT, STB..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de compte *</label>
                    <Input type="text" required value={accountForm.accountNumber} onChange={(e) => setAccountForm({ ...accountForm, accountNumber: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">RIB</label>
                    <Input type="text" value={accountForm.rib} onChange={(e) => setAccountForm({ ...accountForm, rib: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
                    <Input type="text" value={accountForm.iban} onChange={(e) => setAccountForm({ ...accountForm, iban: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Devise</label>
                    <Select value={accountForm.currency} onChange={(e) => setAccountForm({ ...accountForm, currency: e.target.value })}>
                      <option value="TND">TND</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Solde initial</label>
                    <Input type="number" step="0.001" value={accountForm.initialBalance} onChange={(e) => setAccountForm({ ...accountForm, initialBalance: parseFloat(e.target.value) || 0 })} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <Textarea value={accountForm.notes} onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })} rows={3} />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="secondary" onClick={() => setShowAddModal(false)}>Annuler</Button>
                  <Button variant="primary" type="submit">Créer le compte</Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nouvelle Transaction */}
      {showTransactionModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Nouvelle Transaction - {selectedAccount.name}</h2>
              <form onSubmit={handleAddTransaction}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <Select required value={transactionForm.transactionType} onChange={(e) => setTransactionForm({ ...transactionForm, transactionType: e.target.value })}>
                      <option value="income">Encaissement</option>
                      <option value="expense">Décaissement</option>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant *</label>
                    <Input type="number" step="0.001" required value={transactionForm.amount} onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <Input type="date" required value={transactionForm.date} onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                    <Select required value={transactionForm.category} onChange={(e) => setTransactionForm({ ...transactionForm, category: e.target.value })}>
                      <option value="sale">Vente</option>
                      <option value="purchase">Achat</option>
                      <option value="salary">Salaire</option>
                      <option value="rent">Loyer</option>
                      <option value="utilities">Charges</option>
                      <option value="tax">Impôts</option>
                      <option value="loan">Prêt</option>
                      <option value="investment">Investissement</option>
                      <option value="withdrawal">Retrait</option>
                      <option value="deposit">Dépôt</option>
                      <option value="transfer">Virement</option>
                      <option value="other">Autre</option>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <Input type="text" required value={transactionForm.description} onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                    <Input type="text" value={transactionForm.reference} onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })} placeholder="N° chèque, virement..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                    <Select value={transactionForm.paymentMethod} onChange={(e) => setTransactionForm({ ...transactionForm, paymentMethod: e.target.value })}>
                      <option value="transfer">Virement</option>
                      <option value="check">Chèque</option>
                      <option value="card">Carte bancaire</option>
                      <option value="cash">Espèces</option>
                      <option value="other">Autre</option>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <Textarea value={transactionForm.notes} onChange={(e) => setTransactionForm({ ...transactionForm, notes: e.target.value })} rows={2} />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="secondary" onClick={() => setShowTransactionModal(false)}>Annuler</Button>
                  <Button variant="primary" type="submit">Ajouter la transaction</Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <ExportModal
          show={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Exporter les transactions bancaires"
          columns={bankExportColumns}
          data={filteredTransactions}
          filename={`transactions-banque-${selectedAccount?.name || 'comptes'}`}
        />
      )}
    </div>
  );
}